import { useState, useEffect, useRef } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  Bell, LogOut, User, Shield, MessageCircle,
  LayoutDashboard, ShoppingBag, PlusCircle,
  ShoppingCart, Search, MoreHorizontal,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import "./Navbar.css";
import { useSocket } from "../../context/SocketContext";

export default function Navbar() {
  const { user, logout, isAdmin } = useAuth();
  const { unreadChats, chatNotif, newNotif } = useSocket();

  const [menuOpen,       setMenuOpen]       = useState(false);
  const [notifOpen,      setNotifOpen]      = useState(false);
  const [moreOpen,       setMoreOpen]       = useState(false);
  const [mobileMoreOpen, setMobileMoreOpen] = useState(false);
  const [notifs,         setNotifs]         = useState([]);
  const [unreadCount,    setUnreadCount]    = useState(0);

  const menuRef  = useRef(null);
  const notifRef = useRef(null);
  const moreRef  = useRef(null);

  const fetchNotifs = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/users/notifications");
      setNotifs(data.notifications.slice(0, 6));
      setUnreadCount(data.notifications.filter((n) => !n.read).length);
    } catch {}
  };

  useEffect(() => { fetchNotifs(); }, [user]);
  useEffect(() => { if (newNotif) fetchNotifs(); }, [newNotif]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current  && !menuRef.current.contains(e.target))  setMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
      if (moreRef.current  && !moreRef.current.contains(e.target))  setMoreOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMarkRead = async () => {
    try {
      await api.put("/users/notifications/read-all");
      setUnreadCount(0);
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  const closeAll = () => { setMenuOpen(false); setNotifOpen(false); setMoreOpen(false); };

  return (
    <>
      <header className="navbar">

        {/* Logo */}
        <Link to="/app/dashboard" className="navbar-logo">
          <img src="/logo.png" alt="GLA Exchange" className="navbar-logo-img" />
          <span>GLA Exchange</span>
        </Link>

        {/* ── Centre nav links (desktop only) ────────────────── */}
        <nav className="navbar-links">
          <NavLink to="/app/dashboard"
            className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>
            <LayoutDashboard size={16} /> Home
          </NavLink>

          <NavLink to="/app/marketplace"
            className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>
            <ShoppingBag size={16} /> Market
          </NavLink>

          <NavLink to="/app/sell"
            className={({ isActive }) => "navbar-link navbar-link-sell" + (isActive ? " active" : "")}>
            <PlusCircle size={16} /> Sell
          </NavLink>

          <NavLink to="/app/lostfound"
            className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>
            <Search size={16} /> Lost &amp; Found
          </NavLink>

          <NavLink to="/app/cart"
            className={({ isActive }) => "navbar-link" + (isActive ? " active" : "")}>
            <ShoppingCart size={16} /> Cart
          </NavLink>

          {/* More dropdown */}
          <div className="navbar-more-wrap" ref={moreRef}>
            <button
              className={"navbar-link navbar-more-btn" + (moreOpen ? " active" : "")}
              onClick={() => { setMoreOpen((o) => !o); setMenuOpen(false); setNotifOpen(false); }}
            >
              <MoreHorizontal size={16} /> More
            </button>

            {moreOpen && (
              <div className="navbar-more-dropdown fade-in">
                <NavLink to="/app/chat" className="navbar-more-item" onClick={closeAll}>
                  <MessageCircle size={15} />
                  <span>Messages</span>
                  {unreadChats > 0 && (
                    <span className="navbar-more-badge">{unreadChats > 9 ? "9+" : unreadChats}</span>
                  )}
                </NavLink>
                <NavLink to="/app/profile" className="navbar-more-item" onClick={closeAll}>
                  <User size={15} /> Profile
                </NavLink>
                {isAdmin && (
                  <NavLink to="/admin" className="navbar-more-item" onClick={closeAll}>
                    <Shield size={15} /> Admin Portal
                  </NavLink>
                )}
              </div>
            )}
          </div>
        </nav>

        {/* ── Right side ─────────────────────────────────────── */}
        <div className="navbar-right">

          {chatNotif && (
            <div className="chat-notif-banner fade-in">
              <MessageCircle size={14} />
              <span>
                <strong>{chatNotif.senderName}</strong>: {chatNotif.text.slice(0, 30)}
              </span>
            </div>
          )}

          <Link to="/app/chat" className="navbar-icon-btn" style={{ position: "relative" }}>
            <MessageCircle size={20} />
            {unreadChats > 0 && (
              <span className="notif-dot">{unreadChats > 9 ? "9+" : unreadChats}</span>
            )}
          </Link>

          <Link to="/app/cart" className="navbar-icon-btn">
            <ShoppingCart size={20} />
          </Link>

          {/* Bell */}
          <div className="navbar-notif-wrap" ref={notifRef}>
            <button
              className="navbar-icon-btn"
              onClick={() => { setNotifOpen((o) => !o); setMenuOpen(false); setMoreOpen(false); }}
              aria-label="Notifications"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notif-dot">{unreadCount > 9 ? "9+" : unreadCount}</span>
              )}
            </button>

            {notifOpen && (
              <div className="notif-dropdown fade-in">
                <div className="notif-header">
                  <span className="notif-title">Notifications</span>
                  {unreadCount > 0 && (
                    <button className="notif-mark-read" onClick={handleMarkRead}>
                      Mark all read
                    </button>
                  )}
                </div>

                {notifs.length === 0 ? (
                  <div className="notif-empty">No notifications yet</div>
                ) : (
                  notifs.map((n) => (
                    <div key={n._id} className={`notif-item ${!n.read ? "unread" : ""}`}>
                      <div className="notif-item-title">{n.title}</div>
                      <div className="notif-item-msg">{n.message}</div>
                      <div className="notif-item-time">
                        {new Date(n.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Avatar */}
          <div className="navbar-avatar-wrap" ref={menuRef}>
            <button
              className="navbar-avatar-btn"
              onClick={() => { setMenuOpen((o) => !o); setNotifOpen(false); setMoreOpen(false); }}
              aria-label="Account menu"
            >
              {user?.avatar
                ? <img src={user.avatar} alt={user.name} className="avatar avatar-sm" />
                : <div className="avatar-placeholder avatar-sm">{user?.name?.[0]}</div>
              }
            </button>

            {menuOpen && (
              <div className="avatar-dropdown fade-in">
                <div className="avatar-dropdown-user">
                  <span className="avatar-dropdown-name">{user?.name}</span>
                  <span className="avatar-dropdown-email">{user?.email}</span>
                </div>
                <hr className="avatar-dropdown-divider" />
                <Link to="/app/profile" className="avatar-dropdown-item" onClick={closeAll}>
                  <User size={15} /> My Profile
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="avatar-dropdown-item" onClick={closeAll}>
                    <Shield size={15} /> Admin Portal
                  </Link>
                )}
                <button className="avatar-dropdown-item danger" onClick={logout}>
                  <LogOut size={15} /> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Mobile Bottom Tab Bar ───────────────────────────── */}
      <nav className="mobile-bottom-nav">

        <NavLink to="/app/dashboard" className={({ isActive }) => "mobile-tab" + (isActive ? " active" : "")}>
          <LayoutDashboard size={20} />
          <span>Home</span>
        </NavLink>

        <NavLink to="/app/marketplace" className={({ isActive }) => "mobile-tab" + (isActive ? " active" : "")}>
          <ShoppingBag size={20} />
          <span>Market</span>
        </NavLink>

        {/* Centre Sell FAB */}
        <NavLink to="/app/sell" className={({ isActive }) => "mobile-tab mobile-tab-sell" + (isActive ? " active" : "")}>
          <div className="mobile-tab-sell-icon">
            <PlusCircle size={24} />
          </div>
          <span>Sell</span>
        </NavLink>

        <NavLink to="/app/lostfound" className={({ isActive }) => "mobile-tab" + (isActive ? " active" : "")}>
          <Search size={20} />
          <span>Lost &amp; Found</span>
        </NavLink>

        {/* More — opens popup */}
        <div className="mobile-tab-more-wrap">
          <button
            className={"mobile-tab" + (mobileMoreOpen ? " active" : "")}
            onClick={() => setMobileMoreOpen((o) => !o)}
          >
            <MoreHorizontal size={20} />
            <span>More</span>
          </button>

          {mobileMoreOpen && (
            <>
              <div className="mobile-more-backdrop" onClick={() => setMobileMoreOpen(false)} />
              <div className="mobile-more-popup">
                <div className="mobile-more-popup-handle" />
                <Link to="/app/about" className="mobile-more-item" onClick={() => setMobileMoreOpen(false)}>
                  <span className="mobile-more-item-icon">📄</span>
                  <div>
                    <div className="mobile-more-item-title">About</div>
                    <div className="mobile-more-item-sub">Learn about GLA Exchange</div>
                  </div>
                </Link>
                <Link to="/app/install" className="mobile-more-item" onClick={() => setMobileMoreOpen(false)}>
                  <span className="mobile-more-item-icon">📲</span>
                  <div>
                    <div className="mobile-more-item-title">Install App</div>
                    <div className="mobile-more-item-sub">Add to your home screen</div>
                  </div>
                </Link>
              </div>
            </>
          )}
        </div>

      </nav>
    </>
  );
}